const Address = require('@/lib/Addresses/Address');
const Contact = require('@/lib/Contacts/Contact');
const Member = require('@/lib/Memberships/Member');
const MembershipPlan = require('@/lib/Memberships/MembershipPlan');
const { cloneDeep } = require('@/functions/clone');
const { toDate, toNullableInt, toNullableString } = require('@/functions/transform');
const { toYMD } = require('@/functions/date');

/**
 * Represents all the data for a single membership while editing it, including the list of members.
 * This may be an as-yet uncreated membership. Or info from an existing membership being modified.
 *
 * @constructor
 *
 * @param {MembershipPlan} membershipPlan
 */
const MembershipDataset = function (membershipPlan) {
    // It comprises a list of members and an address.
    // The complications are that the first member can be flagged as being the customer,
    // and the address can be flagged as being the customer address.
    // We now allow them to enter the membership ref, in case they give out the card immediately.
    this.assignedMembershipRef = '';

    // These might get overridden if there's a plan and it has an allowed usage date range that starts in the future.
    this.membershipDateFrom = new Date();
    this.allowedUsageStartsInFuture = false;

    /**
     * @type {Member[]}
     */
    this.members = [];

    /**
     * If editing an existing membership this will be populated (by the Membership's toMembershipDataset() method).
     *
     * @type {number|null}
     */
    this.membershipID = null;

    /**
     * @type {MembershipPlan}
     */
    this.membershipPlan = null;

    /**
     * @type {Address}
     */
    this.address = new Address();

    /**
     * If an existing address is being used this value will be populated.
     *
     * @type {number|null}
     */
    this.addressID = null;

    this.isActive = false; // For the tabs in the pop-up.

    /**
     * @type {string|null}
     */
    this.assignedRefErrorMessage = null;

    /**
     * @type {string|null}
     */
    this.currentContactRefErrorMessage = null;

    /**
     * 0 = the membership ref / start data / address page.
     * 1 = the members page.
     *
     * @type {number}
     */
    this.activeDeck = 0;

    this.captureDatesOfBirth = false; // Is populated from membership plan in the cart item below.
    this.maximumPeople = 0;
    this.minimumAdults = 0;
    this.minimumChildren = 0;
    this.memberCutoffAgeYears = null;

    // Initialise the empty list of members, and the address.
    // If we are in the process of restoring the item, we'll automatically add
    // the membership data back in via the hydration.
    if (membershipPlan) {
        // Use a copy of the item and remove the nested items in the category.
        this.membershipPlan = Object.assign(new MembershipPlan(), membershipPlan);

        this.initialiseMembers(membershipPlan.people);
        this.captureDatesOfBirth = membershipPlan.captureDatesOfBirth;
        this.maximumPeople = membershipPlan.people;
        this.minimumChildren = membershipPlan.minimumChildren;
        this.minimumAdults = membershipPlan.minimumAdults;
        this.memberCutoffAgeYears = membershipPlan.memberCutoffAgeYears;
        if (membershipPlan.allowedUsageFromDate != null) {
            if (this.membershipDateFrom < membershipPlan.allowedUsageFromDate) {
                this.membershipDateFrom = membershipPlan.allowedUsageFromDate;
                this.allowedUsageStartsInFuture = true;
            }
        }
    }

    /**
     * The member that is currently being edited.
     *
     * @type {Member}
     */
    this.currentMember = null;

    /**
     * The contact data that is currently being edited.
     * When selecting a member this becomes a clone of the existing contact if there is one,
     * or a new Contact object if there is not.
     * When saving this contact will be set at the contact for this.currentMember.
     *
     * @type {Contact}
     */
    this.currentContact = null;

    /**
     * This is set when selecting a member to the member's contact ID (null if a new member / new contact).
     * The 'Select A Contact' dropdown in the form is bound to this value so will change with that.
     * Upon changing, the selectedExistingContact() method will fire in the controller which will
     * switch out the currentContact property of this dataset.
     * This is so the change is not saved to the member until the save button is pressed.
     */
    this.currentContactID = null;
};

MembershipDataset.prototype = {
    getHydrationMap() {
        return {
            address: {
                model: Address,
            },
            assignedMembershipRef: toNullableString,
            membershipDateFrom: toDate,
            membershipID: toNullableInt,
            membershipPlan: {
                model: MembershipPlan,
            },
            members: {
                modelCollection: Member,
            },
        };
    },

    initialiseMembers: function initialiseMembers(numMembers) {
        // Build the appropriate number of empty Member entities.
        for (let i = 0; i < numMembers; i++) {
            let member = new Member();
            // We create the member but do not give them a contact yet.
            // Memberships in the database will always have the full number of members but some of them
            // may not have contacts.
            // member.contact = new Contact();
            member.setIndex(i);
            this.members.push(member);
        }
    },

    /**
     * @param {Member} member
     */
    selectMember: function selectMember(member) {
        if (!member) {
            this.currentMember = null;
            this.currentContact = null;
            this.currentContactID = null;
            return;
        }

        let contact;
        if (member.contact) {
            contact = cloneDeep(member.contact);
        } else {
            contact = new Contact();
        }
        this.currentContact = contact;
        this.currentMember = member;
        this.currentContactID = contact.ID;
    },

    /**
     * @param {Number} index
     */
    selectMemberAtIndex: function selectMemberAtIndex(index) {
        if (this.members.hasOwnProperty(index)) {
            this.selectMember(this.members[index]);
        } else {
            this.selectMember(null);
        }
    },

    /**
     * Save the data from this.currentContact to the currently selected member.
     *
     * @param selectNext
     * @param {boolean} isTemporary
     */
    saveCurrentMember: function saveCurrentMember(selectNext, isTemporary = false) {
        if (this.currentContact.isEmpty()) {
            this.removeMemberContact(this.currentMember, true);
            return;
        }

        this.currentMember.setContact(this.currentContact);

        if (!isTemporary) {
            // Perform a hard save.
            this.currentMember.saved = true;
        } else {
            this.currentMember.saved = false;
        }

        if (selectNext) {
            // Select next member.
            this.selectMemberAtIndex(this.currentMember.index + 1);
        }
    },

    /**
     * The members will always exist in the database even if there is nobody set for them.
     * So to remove a member we just remove the member's contact.
     *
     * @param {Member} member
     * @param {boolean} staySelected
     */
    removeMemberContact: function removeMemberContact(member, staySelected = false) {
        member.setContact(null);
        member.saved = false;
        if (!staySelected) {
            this.selectMember(null);
        }
    },

    /**
     * Returns all the non-null members from the this.members array.
     *
     * @return {Member[]}
     */
    getMembers: function getMembers() {
        return this.members.filter(function (m) {
            return !!m;
        });
    },

    /**
     * Returns the number of unsaved members.
     *
     * @returns {number}
     */
    countUnsavedMembers() {
        return this.getMembersWithContacts().filter((m) => {
            return !m.saved;
        }).length;
    },

    /**
     * Returns all the members from the this.members array that have a non-null contact.
     *
     * @return {Member[]}
     */
    getMembersWithContacts: function getMembersWithContacts() {
        return this.members.filter(function (m) {
            return !!m && !!m.contact;
        });
    },

    /**
     * @return {Date}
     */
    getDateFrom: function getDateFrom() {
        if (this.membershipDateFrom) {
            return this.membershipDateFrom;
        }

        return (new Date()).setHours(0, 0, 0, 0);
    },

    /**
     * Return only the info that is needed to sent to the API
     * (exclude things to do with the form interface)
     */
    toServerData: function toServerData() {
        let data = {
            address: this.address ? this.address.toServerData() : null,
            assignedMembershipRef: this.assignedMembershipRef, // assignedMembershipRef for new memberships
            ref: this.assignedMembershipRef, // ref for new memberships
            membershipDateFrom: this.membershipDateFrom ? toYMD(this.membershipDateFrom) : null,
            membershipPlanID: this.membershipPlan ? this.membershipPlan.getID() : null,
            membershipID: this.membershipID,
            members: [],
        };
        for (let i = 0; i < this.members.length; i++) {
            let member = this.members[i];
            data.members.push({
                memberID: member.ID,
                contactID: member.contactID,
                contact: member.contact ? member.contact.toServerData() : null,
            });
        }

        return data;
    },
};

module.exports = MembershipDataset;
